import React, { useState, useEffect } from 'react';
import { appDialog } from '../utils/appDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Tag as TagIcon
} from '@mui/icons-material';
import { environment } from '../config/environment';
import { getPetflixAccessToken } from '../config/petflixAuth';

interface PostTag {
  _id: string;
  name?: string;
  contest_id?: string;
  contest_name?: string;
  tag_code?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
}

interface DeleteTagsModalProps {
  open: boolean;
  onClose: () => void;
}

const DeleteTagsModal: React.FC<DeleteTagsModalProps> = ({ open, onClose }) => {
  const [tags, setTags] = useState<PostTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadTags();
    } else {
      // Limpiar estado al cerrar
      setTags([]);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setLoadingProgress('Iniciando carga...');

    try {
      const token = getPetflixAccessToken();
      if (!token) {
        throw new Error('No se encontró token de autenticación. Por favor, inicia sesión.');
      }

      const allTags: PostTag[] = [];
      const limit = 100;
      let skip = 0;
      let hasMoreData = true;

      // Hacer llamadas paginadas hasta que no haya más datos
      while (hasMoreData) {
        setLoadingProgress(`Cargando página ${Math.floor(skip / limit) + 1} (${skip} - ${skip + limit - 1})...`);

        const url = `${environment.petflixApiUrl}/post-tags?$limit=${limit}&$skip=${skip}`;
        console.log(`Cargando tags: ${url}`);

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // La API puede devolver un array directamente o un objeto con data
        let tagsArray: PostTag[] = [];
        if (Array.isArray(data)) {
          tagsArray = data;
        } else if (data.data) {
          tagsArray = Array.isArray(data.data) ? data.data : [];
        } else if (data.results) {
          tagsArray = Array.isArray(data.results) ? data.results : [];
        }

        // Si no hay datos o el array está vacío, detener la paginación
        if (tagsArray.length === 0) {
          hasMoreData = false;
          console.log(`No hay más datos. Total de tags cargados: ${allTags.length}`);
        } else {
          // Agregar los tags a la lista total
          allTags.push(...tagsArray);
          console.log(`Cargados ${tagsArray.length} tags. Total acumulado: ${allTags.length}`);
          
          // Si la respuesta tiene menos de 100 elementos, es la última página
          if (tagsArray.length < limit) {
            hasMoreData = false;
            console.log(`Última página detectada (${tagsArray.length} < ${limit}). Total: ${allTags.length}`);
          } else {
            // Continuar con la siguiente página
            skip += limit;
          }
        }
      }

      setTags(allTags);
      setLoadingProgress(`Carga completada: ${allTags.length} tags encontrados`);
      
      // Limpiar el mensaje de progreso después de 2 segundos
      setTimeout(() => setLoadingProgress(''), 2000);
    } catch (err: any) {
      console.error('Error loading tags:', err);
      setError(err.message || 'Error al cargar los tags');
      setLoadingProgress('');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tagId: string) => {
    if (!(await appDialog.confirm('¿Estás seguro de que deseas eliminar este tag? Esta acción no se puede deshacer.', { destructive: true }))) {
      return;
    }

    setDeleting(tagId);
    setError(null);
    setSuccess(null);

    try {
      const token = getPetflixAccessToken();
      if (!token) {
        throw new Error('No se encontró token de autenticación. Por favor, inicia sesión.');
      }

      const response = await fetch(`${environment.petflixApiUrl}/post-tags/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
      }

      // Eliminar el tag de la lista local
      setTags(prevTags => prevTags.filter(tag => tag._id !== tagId));
      setSuccess('Tag eliminado exitosamente');
      
      // Limpiar el mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error deleting tag:', err);
      setError(err.message || 'Error al eliminar el tag');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: 'bold',
        borderBottom: '1px solid #e5e7eb',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TagIcon sx={{ color: '#dc2626' }} />
          <Typography variant="h6" component="span">
            Eliminar Tags de Concursos
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              {loadingProgress || 'Cargando tags...'}
            </Typography>
          </Box>
        ) : tags.length === 0 ? (
          <Alert severity="info">
            No se encontraron tags de concursos.
          </Alert>
        ) : (
          <>
            {loadingProgress && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {loadingProgress}
              </Alert>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total de tags: <strong>{tags.length}</strong>
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Nombre/Tag Code</strong></TableCell>
                    <TableCell><strong>Concurso ID</strong></TableCell>
                    <TableCell><strong>Nombre del Concurso</strong></TableCell>
                    <TableCell><strong>Fecha de Creación</strong></TableCell>
                    <TableCell><strong>Acciones</strong></TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag._id}>
                    <TableCell>
                      {tag.tag_code ? (
                        <Chip label={tag.tag_code} color="primary" size="small" />
                      ) : (
                        <Typography variant="body2">
                          {tag.name || tag._id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {tag.contest_id || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tag.contest_name || tag.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(tag.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={deleting === tag._id ? <CircularProgress size={16} /> : <DeleteIcon />}
                        onClick={() => handleDelete(tag._id)}
                        disabled={deleting === tag._id}
                      >
                        {deleting === tag._id ? 'Eliminando...' : 'Borrar'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
        <Button onClick={loadTags} variant="contained" disabled={loading}>
          Actualizar Lista
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTagsModal;

